import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Tabs,
  Tab,
  Badge,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  History as HistoryIcon,
  Publish as PublishIcon,
  Block as UnpublishIcon,
  Visibility as VisibilityIcon,
  Code as CodeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { systemPromptsApi } from '../services/aiDiagnosticApi';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
  },
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 2fr',
    gap: theme.spacing(3),
    height: 'calc(100vh - 400px)',
    flex: 1,
  },
  projectsList: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    boxShadow: theme.shadows[1],
    overflowY: 'auto',
  },
  projectItem: {
    cursor: 'pointer',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      boxShadow: theme.shadows[2],
    },
  },
  projectDetails: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    boxShadow: theme.shadows[1],
    overflowY: 'auto',
    height: 'calc(100vh - 400px)', // Fixed height to match left panel
    maxHeight: 'calc(100vh - 400px)',
  },
  createForm: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    boxShadow: theme.shadows[1],
  },
  editForm: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    boxShadow: theme.shadows[1],
    maxHeight: 'calc(100vh - 500px)', // Constrain edit form height
    overflowY: 'auto',
  },
  instructionsTextarea: {
    fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Source Code Pro', monospace",
    fontSize: '14px',
    lineHeight: 1.7,
  },
  previewContainer: {
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    maxHeight: '400px',
    overflowY: 'auto',
  },
  statusBadge: {
    '&.draft': {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
    },
    '&.published': {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
    },
  },
  versionsList: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    maxHeight: 'calc(100vh - 500px)', // Constrain versions list height
    overflowY: 'auto',
  },
  versionItem: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
  noSelection: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    color: theme.palette.text.secondary,
  },
  expandableJson: {
    position: 'relative',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(1, 0),
    backgroundColor: theme.palette.grey[50],
  },
  toggleJsonButton: {
    position: 'absolute',
    top: -32,
    right: 0,
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.grey[600],
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.75rem',
    cursor: 'pointer',
    zIndex: 1,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.grey[800],
    },
  },
  jsonContent: {
    margin: 0,
    padding: theme.spacing(1.25),
    overflowX: 'auto',
    fontFamily: 'monospace',
    lineHeight: 1.4,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
  },
}));

// Expandable JSON component for large payloads
const ExpandableJson = ({ data, classes }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const jsonString = JSON.stringify(data, null, 2);
  const isLargeJson = jsonString.length > 500; // Consider JSON large if over 500 characters

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  if (!isLargeJson) {
    return <pre className={classes.jsonContent}>{jsonString}</pre>;
  }

  return (
    <div className={classes.expandableJson}>
      <Button
        className={classes.toggleJsonButton}
        onClick={toggleExpand}
        size="small"
        variant="contained"
        startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      >
        {isExpanded ? 'Collapse' : 'Expand'} JSON
      </Button>
      <pre className={classes.jsonContent}>
        {isExpanded ? jsonString : jsonString.substring(0, 300) + "... [truncated]"}
      </pre>
    </div>
  );
};

const SystemPrompts = ({ classes, refreshTrigger, claudeToken }) => {
  const styles = useStyles();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [changeDescription, setChangeDescription] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    instructions: '',
    enabledTools: [],
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (claudeToken) {
      fetchProjects();
    }
  }, [claudeToken, refreshTrigger]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isEditing) {
        if (e.ctrlKey && e.key === 's') {
          e.preventDefault();
          handleSave();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          setIsEditing(false);
          setEditingProject(null);
          setChangeDescription('');
          setShowPreview(false);
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isEditing, editingProject, changeDescription]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await systemPromptsApi.getProjects();
      setProjects(data.data || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectVersions = async (projectId) => {
    try {
      const data = await systemPromptsApi.getProjectVersions(projectId);
      setVersions(data.data || []);
    } catch (err) {
      console.error('Error fetching versions:', err);
    }
  };

  const handleCreateProject = async () => {
    try {
      if (!newProject.name || !newProject.instructions) {
        setError('Project name and instructions are required');
        return;
      }

      const createdProject = await systemPromptsApi.createProject(newProject);
      setProjects([createdProject.data, ...projects]);
      setNewProject({ name: '', description: '', instructions: '', enabledTools: [] });
      setShowCreateForm(false);
      setError(null);
      setSelectedProject(createdProject.data);
      setShowVersions(false);
      setEditingProject(null);
      setIsEditing(false);
    } catch (err) {
      console.error('Error creating project:', err);
      setError(err.message || 'An error occurred');
    }
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setShowVersions(false);
    setEditingProject(null);
    setIsEditing(false);
    setChangeDescription('');
    setShowPreview(false);
    setVersions([]);
  };

  const handleShowVersions = async (project) => {
    if (showVersions && selectedProject?._id === project._id) {
      setShowVersions(false);
      setVersions([]);
    } else {
      setSelectedProject(project);
      setShowVersions(true);
      setEditingProject(null);
      setIsEditing(false);
      await fetchProjectVersions(project._id);
    }
  };

  const handleEdit = (project) => {
    const readableInstructions = project.instructions.replace(/\\n/g, '\n');
    setEditingProject({ ...project, instructions: readableInstructions });
    setIsEditing(true);
    setSelectedProject(project);
    setShowVersions(false);
    setShowPreview(false);
  };

  const handleSave = async () => {
    if (!editingProject) return;

    try {
      const { _id, createdAt, updatedAt, ...updateData } = editingProject;

      const updatedProject = await systemPromptsApi.updateProject(editingProject._id, {
        ...updateData,
        changeDescription,
      });
      
      setProjects(projects.map(p => 
        p._id === editingProject._id ? updatedProject.data : p
      ));
      setSelectedProject(updatedProject.data);
      setEditingProject(null);
      setIsEditing(false);
      setChangeDescription('');
      setShowPreview(false);
      await fetchProjects();
    } catch (err) {
      console.error('Error updating project:', err);
      setError(err.message || 'An error occurred');
    }
  };

  const handlePublish = async (project) => {
    try {
      const updatedProject = await systemPromptsApi.publishProject(project._id);
      await fetchProjects();
      if (selectedProject?._id === project._id) {
        setSelectedProject(updatedProject.data);
      }
    } catch (err) {
      console.error('Error publishing project:', err);
      setError(err.message || 'An error occurred');
    }
  };

  const handleUnpublish = async (project) => {
    try {
      const updatedProject = await systemPromptsApi.unpublishProject(project._id);
      await fetchProjects();
      if (selectedProject?._id === project._id) {
        setSelectedProject(updatedProject.data);
      }
    } catch (err) {
      console.error('Error unpublishing project:', err);
      setError(err.message || 'An error occurred');
    }
  };

  const handleRevert = async (projectId, version) => {
    try {
      const updatedProject = await systemPromptsApi.revertProjectToVersion(projectId, version);
      await fetchProjects();
      if (selectedProject?._id === projectId) {
        setSelectedProject(updatedProject.data);
      }
      await fetchProjectVersions(projectId);
    } catch (err) {
      console.error('Error reverting project:', err);
      setError(err.message || 'An error occurred');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    return (
      <Chip
        label={status === 'published' ? '✅ Published' : '📝 Draft'}
        className={`${styles.statusBadge} ${status}`}
        size="small"
      />
    );
  };

  const formatInstructions = (instructions) => {
    if (!instructions) return <div>No instructions available</div>;

    const lines = instructions.replace(/\\n/g, '\n').split('\n');

    return lines.map((line, index) => {
      const trimmedLine = line.trim();

      if (trimmedLine.startsWith('#') || trimmedLine.startsWith('##') || trimmedLine.startsWith('###')) {
        const isMainHeader = trimmedLine.startsWith('#') && !trimmedLine.startsWith('##');
        const isSubHeader = trimmedLine.startsWith('##') && !trimmedLine.startsWith('###');

        return (
          <Typography
            key={index}
            variant={isMainHeader ? 'h5' : isSubHeader ? 'h6' : 'subtitle1'}
            sx={{
              fontWeight: 700,
              color: isMainHeader ? 'primary.dark' : isSubHeader ? 'text.secondary' : 'text.secondary',
              margin: isMainHeader ? '24px 0 16px 0' : isSubHeader ? '20px 0 12px 0' : '16px 0 8px 0',
              padding: '12px 0',
              borderBottom: `2px solid ${isMainHeader ? 'primary.main' : isSubHeader ? 'divider' : 'divider'}`,
              backgroundColor: isMainHeader ? 'grey.50' : 'transparent',
              borderRadius: '6px',
              paddingLeft: '12px',
              paddingRight: '12px',
            }}
          >
            {line}
          </Typography>
        );
      }

      if (trimmedLine.startsWith('-') || trimmedLine.startsWith('*') || trimmedLine.startsWith('•')) {
        return (
          <Box key={index} sx={{ marginLeft: 3, position: 'relative', color: 'text.secondary', padding: '4px 0' }}>
            <Typography variant="body2" sx={{ borderLeft: '2px solid', borderColor: 'primary.main', paddingLeft: 2 }}>
              <span style={{ color: 'primary.main', fontWeight: 'bold' }}>•</span> {line}
            </Typography>
          </Box>
        );
      }

      if (trimmedLine.match(/^\d+\./)) {
        return (
          <Box key={index} sx={{ marginLeft: 3, position: 'relative', color: 'text.secondary', padding: '4px 0' }}>
            <Typography variant="body2" sx={{ borderLeft: '2px solid', borderColor: 'primary.main', paddingLeft: 2 }}>
              <span style={{ color: 'primary.main', fontWeight: 'bold' }}>{trimmedLine.match(/^\d+\./)?.[0] || ''}</span> {line}
            </Typography>
          </Box>
        );
      }

      if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
        return (
          <Box
            key={index}
            sx={{
              background: 'error.light',
              borderLeft: '4px solid',
              borderColor: 'error.main',
              padding: '12px 16px',
              margin: '12px 0',
              borderRadius: '0 8px 8px 0',
              color: 'error.dark',
              fontWeight: 600,
            }}
          >
            {line}
          </Box>
        );
      }

      if (trimmedLine === '') {
        return <Box key={index} sx={{ height: '1.6em', display: 'block' }}>&nbsp;</Box>;
      }

      return (
        <Typography key={index} variant="body2" sx={{ color: 'text.primary', lineHeight: 1.7, marginBottom: 1 }}>
          {line}
        </Typography>
      );
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={styles.root}>
      <Box className={styles.header}>
        <Box>
          <Typography variant="h4" component="h1">
            System Prompts Management
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, marginTop: 1 }}>
            <Chip 
              label={`${projects.filter(p => p.status === 'draft').length} Draft`} 
              color="warning" 
              variant="outlined" 
              size="small" 
            />
            <Chip 
              label={`${projects.filter(p => p.status === 'published').length} Published`} 
              color="success" 
              variant="outlined" 
              size="small" 
            />
            <Chip 
              label={`${projects.length} Total`} 
              color="primary" 
              variant="outlined" 
              size="small" 
            />
          </Box>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : '+ Create System Prompt'}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ marginBottom: 2 }}>
          {error}
        </Alert>
      )}

      {showCreateForm && (
        <Card className={styles.createForm}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Create New System Prompt
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Prompt Name"
                  value={newProject.name}
                  onChange={(e) => setNewProject(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter prompt name..."
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Description"
                  value={newProject.description}
                  onChange={(e) => setNewProject(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter description..."
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={15}
                  label="Instructions"
                  value={newProject.instructions}
                  onChange={(e) => setNewProject(prev => ({ ...prev, instructions: e.target.value }))}
                  placeholder="Enter your system prompt instructions here...

# Header Example
## Sub Header Example

- List item example
- Another list item

**Important text example**

1. Numbered item
2. Another numbered item

Use markdown-style formatting for beautiful display!"
                  variant="outlined"
                  className={styles.instructionsTextarea}
                />
                <Typography variant="caption" color="text.secondary" sx={{ marginTop: 1, display: 'block' }}>
                  💡 <strong>Formatting Tips:</strong> Use # for headers, - for lists, ** for important text, and 1. for numbered lists
                </Typography>
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', marginTop: 3 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setShowCreateForm(false);
                  setNewProject({ name: '', description: '', instructions: '', enabledTools: [] });
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateProject}
              >
                Create System Prompt
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      <Box className={styles.container}>
        <Box className={styles.projectsList}>
          <Typography variant="h6" gutterBottom>
            System Prompts ({filteredProjects.length})
          </Typography>
          
          {/* Search and Filter Bar */}
          <Box sx={{ marginBottom: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search prompts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              sx={{ marginBottom: 1 }}
              InputProps={{
                startAdornment: (
                  <Box sx={{ color: 'text.secondary', marginRight: 1 }}>
                    🔍
                  </Box>
                ),
              }}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label="All"
                onClick={() => setStatusFilter('all')}
                color={statusFilter === 'all' ? 'primary' : 'default'}
                size="small"
                clickable
              />
              <Chip
                label="Draft"
                onClick={() => setStatusFilter('draft')}
                color={statusFilter === 'draft' ? 'primary' : 'default'}
                size="small"
                clickable
              />
              <Chip
                label="Published"
                onClick={() => setStatusFilter('published')}
                color={statusFilter === 'published' ? 'primary' : 'default'}
                size="small"
                clickable
              />
            </Box>
          </Box>
                      {filteredProjects.length === 0 ? (
              <Box sx={{ textAlign: 'center', padding: 4, color: 'text.secondary' }}>
                <Typography variant="h6" gutterBottom>
                  {searchTerm || statusFilter !== 'all' ? 'No prompts found' : 'No system prompts yet'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Try adjusting your search or filters'
                    : 'Create your first system prompt to get started!'
                  }
                </Typography>
                {!searchTerm && statusFilter === 'all' && (
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setShowCreateForm(true)}
                    sx={{ marginTop: 2 }}
                  >
                    Create First Prompt
                  </Button>
                )}
              </Box>
            ) : (
              filteredProjects.map((project) => (
            <Card
              key={project._id}
              className={styles.projectItem}
              onClick={() => handleProjectSelect(project)}
              sx={{
                backgroundColor: selectedProject?._id === project._id ? 'primary.50' : 'background.paper',
                borderColor: selectedProject?._id === project._id ? 'primary.main' : 'divider',
                borderWidth: selectedProject?._id === project._id ? '2px' : '1px',
                boxShadow: selectedProject?._id === project._id ? 2 : 1,
                '&:hover': {
                  backgroundColor: selectedProject?._id === project._id ? 'primary.100' : 'action.hover',
                },
              }}
            >
              <CardContent sx={{ padding: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 1 }}>
                  <Typography variant="h6" component="h3">
                    {project.name}
                  </Typography>
                  {getStatusBadge(project.status)}
                </Box>
                <Box sx={{ display: 'flex', gap: 2, marginBottom: 1 }}>
                  <Chip label={`v${project.currentVersion}`} size="small" variant="outlined" />
                  {project.publishedVersion && (
                    <Chip label={`Published: v${project.publishedVersion}`} size="small" variant="outlined" />
                  )}
                  <Typography variant="caption" color="inherit">
                    {formatDate(project.updatedAt)}
                  </Typography>
                </Box>
                {project.description && (
                  <Typography variant="body2" color="inherit">
                    {project.description}
                  </Typography>
                )}
              </CardContent>
            </Card>
            ))
            )}
        </Box>

        <Box className={styles.projectDetails}>
          {selectedProject ? (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, marginBottom: 2, flexWrap: 'wrap' }}>
                {isEditing ? (
                  <>
                    <Tooltip title="Esc to cancel quickly">
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={() => {
                          setIsEditing(false);
                          setEditingProject(null);
                          setChangeDescription('');
                          setShowPreview(false);
                        }}
                      >
                        Cancel Edit
                      </Button>
                    </Tooltip>
                    <Tooltip title="Ctrl+S to save quickly">
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                      >
                        Save Changes
                      </Button>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(selectedProject)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<HistoryIcon />}
                      onClick={() => handleShowVersions(selectedProject)}
                    >
                      {showVersions ? 'Hide Versions' : 'Show Versions'}
                    </Button>
                    {selectedProject.status === 'draft' ? (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<PublishIcon />}
                        onClick={() => handlePublish(selectedProject)}
                      >
                        Publish
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<UnpublishIcon />}
                        onClick={() => handleUnpublish(selectedProject)}
                      >
                        Unpublish
                      </Button>
                    )}
                  </>
                )}
              </Box>

              {isEditing ? (
                <Card className={styles.editForm}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <Typography variant="h6">
                        Edit System Prompt Instructions
                      </Typography>
                      <Button
                        variant={showPreview ? 'contained' : 'outlined'}
                        startIcon={<VisibilityIcon />}
                        onClick={() => setShowPreview(!showPreview)}
                      >
                        {showPreview ? 'Hide Preview' : 'Show Preview'}
                      </Button>
                    </Box>

                    {showPreview ? (
                      <Box className={styles.previewContainer}>
                        <Typography variant="subtitle2" gutterBottom>
                          Preview:
                        </Typography>
                        <Box sx={{ fontFamily: 'monospace', fontSize: '14px', lineHeight: 1.6 }}>
                          {formatInstructions(editingProject?.instructions || '')}
                        </Box>
                      </Box>
                    ) : (
                      <TextField
                        fullWidth
                        multiline
                        rows={20}
                        label="Instructions"
                        value={editingProject?.instructions || ''}
                        onChange={(e) => setEditingProject(prev => 
                          prev ? { ...prev, instructions: e.target.value } : null
                        )}
                        placeholder="Enter your system prompt instructions here..."
                        variant="outlined"
                        className={styles.instructionsTextarea}
                      />
                    )}

                    <TextField
                      fullWidth
                      label="Change Description"
                      value={changeDescription}
                      onChange={(e) => setChangeDescription(e.target.value)}
                      placeholder="Describe what changed in this version..."
                      variant="outlined"
                      sx={{ marginTop: 2 }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', marginTop: 3 }}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setIsEditing(false);
                          setEditingProject(null);
                          setChangeDescription('');
                          setShowPreview(false);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                      >
                        Save Changes
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ) : showVersions ? (
                <Box className={styles.versionsList}>
                  <Typography variant="h6" gutterBottom>
                    Version History
                  </Typography>
                  {versions.map((version) => (
                    <Card key={version._id} className={styles.versionItem}>
                      <CardContent sx={{ padding: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', marginBottom: 1 }}>
                          <Chip label={`v${version.version}`} color="primary" />
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(version.createdAt)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            by {version.createdBy}
                          </Typography>
                        </Box>
                        {version.changeDescription && (
                          <Typography variant="body2" sx={{ marginBottom: 2 }}>
                            {version.changeDescription}
                          </Typography>
                        )}
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleRevert(selectedProject._id, version.version)}
                        >
                          Revert to This Version
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              ) : (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Instructions
                  </Typography>
                  <Paper sx={{ padding: 2, marginBottom: 2, backgroundColor: 'grey.50' }}>
                    <Box sx={{ fontFamily: 'monospace', fontSize: '14px', lineHeight: 1.6 }}>
                      {formatInstructions(selectedProject.instructions)}
                    </Box>
                  </Paper>

                  <Typography variant="h6" gutterBottom>
                    Prompt Details
                  </Typography>
                  <Paper sx={{ padding: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Status:</strong> {getStatusBadge(selectedProject.status)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Current Version:</strong> {selectedProject.currentVersion}
                        </Typography>
                      </Grid>
                      {selectedProject.publishedVersion && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2">
                            <strong>Published Version:</strong> {selectedProject.publishedVersion}
                          </Typography>
                        </Grid>
                      )}
                      {selectedProject.publishedAt && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2">
                            <strong>Published:</strong> {formatDate(selectedProject.publishedAt)}
                          </Typography>
                        </Grid>
                      )}
                      {selectedProject.publishedBy && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2">
                            <strong>Published By:</strong> {selectedProject.publishedBy}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Created:</strong> {formatDate(selectedProject.createdAt)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Updated:</strong> {formatDate(selectedProject.updatedAt)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Display enabled tools and other metadata with expandable JSON */}
                  {selectedProject.enabledTools && selectedProject.enabledTools.length > 0 && (
                    <>
                      <Typography variant="h6" gutterBottom sx={{ marginTop: 3 }}>
                        Enabled Tools
                      </Typography>
                      <Paper sx={{ padding: 2, marginBottom: 2 }}>
                        <ExpandableJson data={selectedProject.enabledTools} classes={styles} />
                      </Paper>
                    </>
                  )}

                  {/* Display full project data for debugging/development */}
                  <Typography variant="h6" gutterBottom sx={{ marginTop: 3 }}>
                    Raw Project Data
                  </Typography>
                  <Paper sx={{ padding: 2 }}>
                    <ExpandableJson data={selectedProject} classes={styles} />
                  </Paper>
                </Box>
              )}
            </Box>
          ) : (
            <Box className={styles.noSelection}>
              <Typography variant="h6" color="text.secondary">
                Select a system prompt to view details
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SystemPrompts;
